import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Calendar, Check, Trash2, Plus, AlertTriangle } from 'react-feather';
import { apiClient } from '../../shared/api/client';

interface PlannedExpense {
  id: number;
  description: string;
  amount: number;
  dueDate: string | null;
  categoryId: number | null;
  category: string | null;
  priority: number;
  status: 'PENDING' | 'COMPLETED';
  isRecurring: boolean;
  recurringType?: string | null;
}

interface CategoryOption {
  id: number;
  name: string;
}

const emptyForm = {
  description: '',
  amount: '',
  dueDate: '',
  categoryId: '',
  priority: '3',
  isRecurring: false,
  recurringType: 'MONTHLY',
};

const PlannedExpensesPage: React.FC = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState<PlannedExpense[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [expensesRes, categoriesRes] = await Promise.all([
        apiClient.get('/planned-expenses'),
        apiClient.get('/categories'),
      ]);
      setExpenses((expensesRes as any).plannedExpenses || []);
      const cats = ((categoriesRes as any).categories || []).flatMap((c: any) => [
        { id: c.id, name: c.name },
        ...(c.children || []).map((ch: any) => ({ id: ch.id, name: `${c.name} › ${ch.name}` })),
      ]);
      setCategories(cats);
    } catch (err: any) {
      console.error('Failed to load planned expenses:', err);
      setError('Kunde inte hämta planerade utgifter. Försök igen.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.description || !form.amount) return;
    try {
      setSaving(true);
      await apiClient.post('/planned-expenses', {
        description: form.description,
        amount: parseFloat(form.amount),
        dueDate: form.dueDate || null,
        categoryId: form.categoryId ? parseInt(form.categoryId) : null,
        priority: parseInt(form.priority),
        isRecurring: form.isRecurring,
        recurringType: form.isRecurring ? form.recurringType : null,
      });
      setForm(emptyForm);
      setShowForm(false);
      await loadData();
    } catch (err) {
      console.error('Failed to create planned expense:', err);
      setError('Kunde inte spara utgiften. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (expense: PlannedExpense) => {
    try {
      await apiClient.post(`/planned-expenses/${expense.id}/complete`);
      await loadData();
    } catch (err) {
      console.error('Failed to complete planned expense:', err);
      setError('Kunde inte markera utgiften som betald.');
    }
  };

  const handleDelete = async (expense: PlannedExpense) => {
    if (!window.confirm(`Ta bort "${expense.description}"?`)) return;
    try {
      await apiClient.delete(`/planned-expenses/${expense.id}`);
      await loadData();
    } catch (err) {
      console.error('Failed to delete planned expense:', err);
      setError('Kunde inte ta bort utgiften.');
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
    });
  };

  const getPriorityColor = (priority: number) => {
    if (priority <= 2) return 'bg-red-500';
    if (priority <= 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPriorityText = (priority: number) => {
    if (priority <= 2) return 'Hög';
    if (priority <= 4) return 'Medel';
    return 'Låg';
  };

  const getStatusColor = (status: string) => {
    return status === 'COMPLETED' ? 'bg-green-500' : 'bg-yellow-500';
  };

  const getStatusText = (status: string) => {
    return status === 'COMPLETED' ? 'Betalad' : 'Väntande';
  };

  const getDaysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getUrgencyColor = (daysUntil: number) => {
    if (daysUntil < 0) return 'bg-red-500'; // Overdue
    if (daysUntil <= 3) return 'bg-yellow-500'; // Due soon
    return 'bg-green-500'; // Not urgent
  };

  const getUrgencyText = (daysUntil: number) => {
    if (daysUntil < 0) return 'Försenad';
    if (daysUntil === 0) return 'Idag';
    if (daysUntil === 1) return 'Imorgon';
    return `${daysUntil} dagar`;
  };

  const renderExpense = (expense: PlannedExpense) => {
    const daysUntil = getDaysUntilDue(expense.dueDate);

    return (
      <div key={expense.id} className="bg-white rounded-xl p-4 shadow-sm mb-3">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className={`text-base font-semibold mb-1 ${expense.status === 'COMPLETED' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
              {expense.description}
            </h3>
            {expense.category && (
              <p className="text-sm text-blue-600 font-medium">{expense.category}</p>
            )}
          </div>
          <div className="flex items-center">
            <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white mr-2 ${getStatusColor(expense.status)}`}>
              {getStatusText(expense.status)}
            </span>
            {expense.isRecurring && (
              <RefreshCw size={14} className="text-gray-500" />
            )}
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex-1">
            <p className={`text-lg font-bold ${expense.status === 'COMPLETED' ? 'line-through text-gray-500' : 'text-red-600'}`}>
              {formatAmount(expense.amount)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {expense.dueDate && (
              <div className="flex items-center">
                <Calendar size={13} className="text-gray-500 mr-1" />
                <span className="text-sm text-gray-600">
                  {formatDate(expense.dueDate)}
                </span>
              </div>
            )}
            {expense.status === 'PENDING' && daysUntil !== null && (
              <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getUrgencyColor(daysUntil)}`}>
                {getUrgencyText(daysUntil)}
              </span>
            )}
            <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getPriorityColor(expense.priority)}`}>
              {getPriorityText(expense.priority)}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-gray-100">
          {expense.status === 'PENDING' && (
            <button
              onClick={() => handleComplete(expense)}
              className="px-3 py-1 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            >
              <Check size={14} className="inline mr-1 -mt-0.5" />Markera betald
            </button>
          )}
          <button
            onClick={() => handleDelete(expense)}
            className="px-3 py-1 text-sm text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 size={14} className="inline mr-1 -mt-0.5" />Ta bort
          </button>
        </div>
      </div>
    );
  };

  const upcomingExpenses = expenses.filter(e => e.status === 'PENDING');
  const completedExpenses = expenses.filter(e => e.status === 'COMPLETED');
  const totalUpcoming = upcomingExpenses.reduce((sum, e) => sum + e.amount, 0);
  const totalCompleted = completedExpenses.reduce((sum, e) => sum + e.amount, 0);

  // Calculate overdue expenses
  const overdueExpenses = upcomingExpenses.filter(e => {
    const days = getDaysUntilDue(e.dueDate);
    return days !== null && days < 0;
  });
  const overdueAmount = overdueExpenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="page-header bg-white shadow-sm border-b border-gray-200">
        <div className="page-header-content px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/settings')} className="mr-4">
              <span className="text-xl">‹</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Planerade utgifter</h1>
          </div>
        </div>
      </div>

      <div className="page-content px-4 py-6">
        <p className="text-gray-600 mb-6">Hantera kommande betalningar och återkommande utgifter</p>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-lg">
            <span className="text-red-800 text-sm">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Laddar planerade utgifter…</p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                <p className="text-sm text-gray-600 mb-2">Kommande utgifter</p>
                <p className="text-xl font-bold text-red-600 mb-1">{formatAmount(totalUpcoming)}</p>
                <p className="text-xs text-gray-500">{upcomingExpenses.length} utgifter</p>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                <p className="text-sm text-gray-600 mb-2">Betalade utgifter</p>
                <p className="text-xl font-bold text-green-600 mb-1">{formatAmount(totalCompleted)}</p>
                <p className="text-xs text-gray-500">{completedExpenses.length} utgifter</p>
              </div>
            </div>

            {/* Overdue Warning */}
            {overdueExpenses.length > 0 && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangle size={18} className="text-red-500 mr-2" />
                  <span className="text-red-800 text-sm font-medium">
                    {overdueExpenses.length} försenad{overdueExpenses.length !== 1 ? 'a' : ''} utgift{overdueExpenses.length !== 1 ? 'er' : ''}
                    ({formatAmount(overdueAmount)})
                  </span>
                </div>
              </div>
            )}

            {/* Expenses List */}
            <div className="mb-6">
              {expenses.length > 0 ? (
                expenses.map(renderExpense)
              ) : (
                <div className="text-center py-12 bg-white rounded-xl">
                  <Calendar size={44} className="mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Inga planerade utgifter</h3>
                  <p className="text-gray-600">Lägg till kommande betalningar för att få en prognos över nästa månad</p>
                </div>
              )}
            </div>

            {/* Add Expense Form */}
            {showForm ? (
              <form onSubmit={handleCreate} className="bg-white rounded-xl p-5 shadow-sm mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ny planerad utgift</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Beskrivning (t.ex. Hyra)"
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Belopp (kr)"
                      value={form.amount}
                      onChange={e => setForm({ ...form, amount: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      required
                    />
                    <input
                      type="date"
                      value={form.dueDate}
                      onChange={e => setForm({ ...form, dueDate: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={form.categoryId}
                      onChange={e => setForm({ ...form, categoryId: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                    >
                      <option value="">Ingen kategori</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <select
                      value={form.priority}
                      onChange={e => setForm({ ...form, priority: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                    >
                      <option value="1">Hög prioritet</option>
                      <option value="3">Medel prioritet</option>
                      <option value="5">Låg prioritet</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={form.isRecurring}
                        onChange={e => setForm({ ...form, isRecurring: e.target.checked })}
                        className="mr-2"
                      />
                      <RefreshCw size={13} className="inline mr-1" /> Återkommande
                    </label>
                    {form.isRecurring && (
                      <select
                        value={form.recurringType}
                        onChange={e => setForm({ ...form, recurringType: e.target.value })}
                        className="border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm"
                      >
                        <option value="MONTHLY">Varje månad</option>
                        <option value="QUARTERLY">Varje kvartal</option>
                        <option value="YEARLY">Varje år</option>
                      </select>
                    )}
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Sparar…' : 'Spara'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setForm(emptyForm); }}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Avbryt
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setShowForm(true)}
                className="w-full bg-white border-2 border-dashed border-blue-600 text-blue-600 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
              >
                <Plus size={16} className="inline mr-1 -mt-0.5" />Lägg till planerad utgift
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlannedExpensesPage;
